/**
 * План работ — личная очередь задач (DEV-1656).
 *
 * Стек живёт на сервере: в нём порядок, в котором человек собрался работать, и
 * этот порядок один на все его устройства. Поэтому каждая правка возвращает
 * стек целиком — место новой задачи меняет номера соседей, и клиенту всё равно
 * пришлось бы перечитать список.
 *
 * Кандидатов ищем существующими ручками задач: отдельного поиска «во что бы
 * поработать» на сервере нет и не нужно — это те же задачи, просто ещё не в
 * плане.
 */
import { issueApi, planApi } from '@/api'
import { listMine, type MyQuery } from '@/features/my/api'

import type { V1IssueType, V1Priority, V1StatusCategory } from '@/api/clients/tracker-issue'
import type { TrackerStackItem } from '@/api/clients/tracker-plan'

/** Строка стека — ровно то, что нужно нарисовать на карточке */
export interface StackItem {
  issueId: string
  key: string
  title: string
  type: V1IssueType | ''
  priority: V1Priority | ''
  /** Момент времени от сервера; в строке от него остаётся день */
  dueDate: string
  /** Колонка задачи — она же её статус */
  columnName: string
  statusCategory: V1StatusCategory | ''
  /**
   * Своя ли задача. В стеке может лежать и чужая — так планируют «жду вот
   * это»; строка помечает такую, чтобы её не путали со своей работой.
   */
  assignedToMe: boolean
  addedAt: string
}

const toItem = (item: TrackerStackItem): StackItem => ({
  issueId: item.issueId ?? '',
  key: item.key ?? '',
  title: item.title ?? '',
  type: (item.type ?? '') as V1IssueType | '',
  priority: (item.priority ?? '') as V1Priority | '',
  dueDate: item.dueDate ?? '',
  columnName: item.columnName ?? '',
  statusCategory: (item.statusCategory ?? '') as V1StatusCategory | '',
  assignedToMe: item.assignedToMe ?? false,
  addedAt: item.addedAt ?? '',
})

export const getStack = async (): Promise<StackItem[]> => {
  const { data } = await planApi.planServiceGetStack()

  return (data.items ?? []).map(toItem)
}

/** Куда класть задачу: наверх, перед конкретной или в конец */
export interface PushPlace {
  top?: boolean
  before?: string
}

export const pushToStack = async (issueKey: string, place: PushPlace = {}): Promise<StackItem[]> => {
  const { data } = await planApi.planServicePushToStack({
    issueKey,
    top: place.top,
    beforeIssueKey: place.before,
  })

  return (data.items ?? []).map(toItem)
}

export const removeFromStack = async (issueKey: string): Promise<StackItem[]> => {
  const { data } = await planApi.planServiceRemoveFromStack(issueKey)

  return (data.items ?? []).map(toItem)
}

/**
 * Порядок уходит целиком, а не «переставь эту сюда»: сервер расставляет позиции
 * по присланному списку и отвергает неполный — иначе задача, о которой клиент
 * забыл, молча уехала бы в конец.
 */
export const reorderStack = async (issueKeys: string[]): Promise<StackItem[]> => {
  const { data } = await planApi.planServiceReorderStack({ issueKeys })

  return (data.items ?? []).map(toItem)
}

/** Цифры плана: гашение по дням, цикл, просрочки и прогноз */
export interface PlanStats {
  burndown: { day: string; closed: number }[]
  medianCycleHours: number
  prevMedianCycleHours: number
  cycleSample: number
  overdue: number
  dueSoon: number
  stackSize: number
  closedPerDay: number
  forecastDays: number
  /** Ширина окон в днях — подписи виджетов берут её отсюда, а не хардкодят */
  burndownDays: number
  cycleDays: number
}

export const getStats = async (): Promise<PlanStats> => {
  const { data } = await planApi.planServiceGetMyStats()

  return {
    burndown: (data.burndown ?? []).map((entry) => ({
      day: entry.day ?? '',
      closed: Number(entry.closed ?? 0),
    })),
    medianCycleHours: Number(data.medianCycleHours ?? 0),
    prevMedianCycleHours: Number(data.prevMedianCycleHours ?? 0),
    cycleSample: Number(data.cycleSample ?? 0),
    overdue: Number(data.overdue ?? 0),
    dueSoon: Number(data.dueSoon ?? 0),
    stackSize: Number(data.stackSize ?? 0),
    closedPerDay: Number(data.closedPerDay ?? 0),
    forecastDays: Number(data.forecastDays ?? 0),
    burndownDays: Number(data.burndownDays ?? 0),
    cycleDays: Number(data.cycleDays ?? 0),
  }
}

/** Карточка кандидата — то же, что строка стека, только ещё не в нём */
export interface Candidate {
  id: string
  key: string
  title: string
  type: V1IssueType | ''
  dueDate: string
}

/** Пресеты панели кандидатов — три вопроса, которые задают чаще всего */
export type CandidatePreset = 'mine' | 'hot' | 'recent'

/** Сколько кандидатов показываем: панель узкая, дальше человек уточняет поиск */
const CANDIDATE_LIMIT = 20

/** «Горящие» — те, чей срок наступает на неделе */
const HOT_DAYS = 7

const OPEN_CATEGORIES: V1StatusCategory[] = [
  'STATUS_CATEGORY_OPEN',
  'STATUS_CATEGORY_IN_PROGRESS',
] as V1StatusCategory[]

const presetQuery = (preset: CandidatePreset, now = new Date()): MyQuery => {
  if (preset === 'hot') {
    const edge = new Date(now.getFullYear(), now.getMonth(), now.getDate() + HOT_DAYS + 1)

    return { statusCategories: OPEN_CATEGORIES, dueBefore: edge.toISOString(), sortBy: 'due_date' }
  }

  if (preset === 'recent') {
    return { sortBy: 'updated_at', sortDesc: true }
  }

  return { statusCategories: OPEN_CATEGORIES, sortBy: 'due_date' }
}

/**
 * Кандидаты пресета — свои задачи, отобранные сервером. Наблюдаемые сюда не
 * идут: в план кладут работу, а не подписки.
 */
export const listCandidates = async (
  preset: CandidatePreset,
  now = new Date(),
): Promise<Candidate[]> => {
  const issues = await listMine(false, presetQuery(preset, now))

  return issues.slice(0, CANDIDATE_LIMIT).map((issue) => ({
    id: issue.id,
    key: issue.key,
    title: issue.title,
    type: issue.type,
    dueDate: issue.dueDate,
  }))
}

/**
 * Поиск кандидата по тексту — полнотекстовый поиск трекера. Он ищет по всем
 * проектам, куда человек допущен: в план кладут и чужие задачи, а «свои
 * открытые» — это уже пресет рядом.
 */
export const searchCandidates = async (query: string): Promise<Candidate[]> => {
  const text = query.trim()

  if (!text) return []

  const { data } = await issueApi.issueServiceSearchIssues(
    text,
    undefined, // projectId — ищем по всем доступным
    '1',
    String(CANDIDATE_LIMIT),
  )

  return (data.issues ?? []).map((issue) => ({
    id: issue.id ?? '',
    key: issue.key ?? '',
    title: issue.title ?? '',
    type: (issue.type ?? '') as V1IssueType | '',
    dueDate: issue.dueDate ?? '',
  }))
}
